import { Server } from '@modelcontextprotocol/sdk/server/index.js';
import { StdioServerTransport } from '@modelcontextprotocol/sdk/server/stdio.js';
import {
  CallToolRequestSchema,
  ListToolsRequestSchema,
} from '@modelcontextprotocol/sdk/types.js';
import { createRequire } from 'module';
import { fileURLToPath } from 'url';
import { dirname, join } from 'path';
import { tools } from './tools.js';
import { VectorDB } from '../vectordb/lancedb.js';
import { LocalEmbeddings } from '../embeddings/local.js';
import { GitStateTracker } from '../git/tracker.js';
import { indexMultipleFiles, indexSingleFile } from '../indexer/incremental.js';
import { configService } from '../config/service.js';
import { ManifestManager } from '../indexer/manifest.js';
import { isGitAvailable, isGitRepo } from '../git/utils.js';
import { FileWatcher } from '../watcher/index.js';
import { VERSION_CHECK_INTERVAL_MS } from '../constants.js';
import { wrapToolHandler } from './utils/tool-wrapper.js';
import {
  SemanticSearchSchema,
  FindSimilarSchema,
  GetFileContextSchema,
  ListFunctionsSchema,
} from './schemas/index.js';
import { LienError, LienErrorCode } from '../errors/index.js';

// Get version from package.json dynamically
const __filename = fileURLToPath(import.meta.url);
const __dirname = dirname(__filename);
const require = createRequire(import.meta.url);

let packageJson: { name: string; version: string };
try {
  packageJson = require(join(__dirname, '../package.json'));
} catch {
  packageJson = require(join(__dirname, '../../package.json'));
}

export interface MCPServerOptions {
  rootDir: string;
  verbose?: boolean;
  watch?: boolean;
}

export async function startMCPServer(options: MCPServerOptions): Promise<void> {
  const { rootDir, verbose, watch } = options;
  
  // Log to stderr (stdout is reserved for MCP protocol)
  const log = (message: string) => {
    if (verbose) {
      console.error(`[Lien MCP] ${message}`);
    }
  };
  
  log('Initializing MCP server...');
  
  // Initialize embeddings and vector DB
  const embeddings = new LocalEmbeddings();
  const vectorDB = new VectorDB(rootDir);
  
  try {
    log('Loading embedding model...');
    await embeddings.initialize();
    
    log('Loading vector database...');
    await vectorDB.initialize();
    
    log('Embeddings and vector DB ready');
  } catch (error) {
    console.error(`Failed to initialize: ${error}`);
    process.exit(1);
  }
  
  // Create MCP server
  const server = new Server(
    {
      name: 'lien',
      version: packageJson.version,
    },
    {
      capabilities: {
        tools: {},
      },
    }
  );
  
  // Register tool list handler
  server.setRequestHandler(ListToolsRequestSchema, async () => ({
    tools,
  }));
  
  // Helper function to check version and reconnect if needed
  const checkAndReconnect = async () => {
    try {
      const versionChanged = await vectorDB.checkVersion();
      if (versionChanged) {
        log('Index version changed, reconnecting to database...');
        await vectorDB.reconnect();
        log('Reconnected to updated index');
      }
    } catch (error) {
      // Log but don't throw - fall back to existing connection
      log(`Version check failed: ${error}`);
    }
  };
  
  // Helper to get current index metadata for responses
  const getIndexMetadata = () => ({
    indexVersion: vectorDB.getCurrentVersion(),
    indexDate: vectorDB.getVersionDate(),
  });
  
  // Start background polling for version changes (every 2 seconds)
  // This ensures we reconnect as soon as possible after reindex, even if no tool calls are made
  const versionCheckInterval = setInterval(async () => {
    await checkAndReconnect();
  }, VERSION_CHECK_INTERVAL_MS);
  
  // Register tool call handler
  server.setRequestHandler(CallToolRequestSchema, async (request) => {
    const { name, arguments: args } = request.params;
    
    log(`Handling tool call: ${name}`);
    
    try {
      switch (name) {
      case 'semantic_search':
        return await wrapToolHandler(
          SemanticSearchSchema,
          async (validatedArgs) => {
            log(`Searching for: "${validatedArgs.query}"`);
            
            // Check if index has been updated and reconnect if needed
            await checkAndReconnect();
            
            const queryEmbedding = await embeddings.embed(validatedArgs.query);
            const results = await vectorDB.search(queryEmbedding, validatedArgs.limit, validatedArgs.query);
            
            log(`Found ${results.length} results`);
            
            return {
              indexInfo: getIndexMetadata(),
              results,
            };
          }
        )(args);
      
      case 'find_similar':
        return await wrapToolHandler(
          FindSimilarSchema,
          async (validatedArgs) => {
            log(`Finding similar code...`);
            
            // Check if index has been updated and reconnect if needed
            await checkAndReconnect();
            
            const codeEmbedding = await embeddings.embed(validatedArgs.code);
            // Pass code as query for relevance boosting
            const results = await vectorDB.search(codeEmbedding, validatedArgs.limit, validatedArgs.code);
            
            log(`Found ${results.length} similar chunks`);
            
            return {
              indexInfo: getIndexMetadata(),
              results,
            };
          }
        )(args);
      
      case 'get_file_context':
        return await wrapToolHandler(
          GetFileContextSchema,
          async (validatedArgs) => {
            log(`Getting context for: ${validatedArgs.filepath}`);
            
            // Check if index has been updated and reconnect if needed
            await checkAndReconnect();
            
            // Search for chunks from this file by embedding the filepath
            // This is a simple approach; could be improved with metadata filtering
            const fileEmbedding = await embeddings.embed(validatedArgs.filepath);
            const allResults = await vectorDB.search(fileEmbedding, 50, validatedArgs.filepath);
            
            // Filter results to only include chunks from the target file
            const fileChunks = allResults.filter(r => 
              r.metadata.file.includes(validatedArgs.filepath) || validatedArgs.filepath.includes(r.metadata.file)
            );
            
            let results = fileChunks;
            
            if (validatedArgs.includeRelated && fileChunks.length > 0) {
              // Get related chunks by searching with the first chunk's content
              const relatedEmbedding = await embeddings.embed(fileChunks[0].content);
              const related = await vectorDB.search(relatedEmbedding, 5, fileChunks[0].content);
              
              // Add related chunks that aren't from the same file
              const relatedOtherFiles = related.filter(r => 
                !r.metadata.file.includes(validatedArgs.filepath) && !validatedArgs.filepath.includes(r.metadata.file)
              );
              
              results = [...fileChunks, ...relatedOtherFiles];
            }
            
            log(`Found ${results.length} chunks`);
            
            return {
              indexInfo: getIndexMetadata(),
              file: validatedArgs.filepath,
              chunks: results,
            };
          }
        )(args);
      
      case 'list_functions':
        return await wrapToolHandler(
          ListFunctionsSchema,
          async (validatedArgs) => {
            log('Listing functions with symbol metadata...');
            
            // Check if index has been updated and reconnect if needed
            await checkAndReconnect();
            
            let results;
            let usedMethod = 'symbols';
            
            try {
              // Try using symbol-based query first (v0.5.0+)
              results = await vectorDB.querySymbols({
                language: validatedArgs.language,
                pattern: validatedArgs.pattern,
                limit: 50,
              });
              
              // If no results and pattern was provided, it might be an old index
              // Fall back to content scanning
              if (results.length === 0 && (validatedArgs.language || validatedArgs.pattern)) {
                log('No symbol results, falling back to content scan...');
                results = await vectorDB.scanWithFilter({
                  language: validatedArgs.language,
                  pattern: validatedArgs.pattern,
                  limit: 50,
                });
                usedMethod = 'content';
              }
            } catch (error) {
              // If querySymbols fails (e.g., old index without symbol fields), fall back
              log(`Symbol query failed, falling back to content scan: ${error}`);
              results = await vectorDB.scanWithFilter({
                language: validatedArgs.language,
                pattern: validatedArgs.pattern,
                limit: 50,
              });
              usedMethod = 'content';
            }
            
            log(`Found ${results.length} matches using ${usedMethod} method`);
            
            return {
              indexInfo: getIndexMetadata(),
              method: usedMethod,
              results,
              note: usedMethod === 'content' 
                ? 'Using content search. Run "lien reindex" to enable faster symbol-based queries.'
                : undefined,
            };
          }
        )(args);
      
      default:
        throw new LienError(
          `Unknown tool: ${name}`,
          LienErrorCode.INVALID_INPUT,
          { requestedTool: name, availableTools: tools.map(t => t.name) },
          'medium',
          false,
          false
        );
      }
    } catch (error) {
      // Handle errors at the switch level (e.g., unknown tool)
      if (error instanceof LienError) {
        return {
          isError: true,
          content: [{
            type: 'text' as const,
            text: JSON.stringify(error.toJSON(), null, 2),
          }],
        };
      }
      
      // Unexpected error
      console.error(`Unexpected error handling tool call ${name}:`, error);
      return {
        isError: true,
        content: [{
          type: 'text' as const,
          text: JSON.stringify({
            error: error instanceof Error ? error.message : 'Unknown error',
            code: LienErrorCode.INTERNAL_ERROR,
            tool: name,
          }, null, 2),
        }],
      };
    }
  });
  
  // Load configuration for auto-indexing, git detection, and file watching
  const config = await configService.load(rootDir);
  
  // Check if this is the first run (no data in index) and auto-index if needed
  const hasIndex = await vectorDB.hasData();
  
  if (!hasIndex && config.mcp.autoIndexOnFirstRun) {
    log('📦 No index found - running initial indexing...');
    log('⏱️  This may take 5-20 minutes depending on project size');
    
    try {
      // Import indexCodebase function
      const { indexCodebase } = await import('../indexer/index.js');
      await indexCodebase({ rootDir, verbose: true });
      log('✅ Initial indexing complete!');
    } catch (error) {
      log(`⚠️  Initial indexing failed: ${error}`);
      log('You can manually run: lien index');
      // Don't exit - server can still start, just won't have data
    }
  } else if (!hasIndex) {
    log('⚠️  No index found. Auto-indexing is disabled in config.');
    log('Run "lien index" to index your codebase.');
  }
  
  // Initialize git detection if enabled
  let gitTracker: GitStateTracker | null = null;
  let gitPollInterval: NodeJS.Timeout | null = null;
  let fileWatcher: FileWatcher | null = null;
  
  if (config.gitDetection.enabled) {
    const gitAvailable = await isGitAvailable();
    const isRepo = await isGitRepo(rootDir);
    
    if (gitAvailable && isRepo) {
      log('✓ Detected git repository');
      gitTracker = new GitStateTracker(rootDir, vectorDB.dbPath);
      
      // Check for git changes on startup
      try {
        log('Checking for git changes...');
        const changedFiles = await gitTracker.initialize();
        
        if (changedFiles && changedFiles.length > 0) {
          log(`🌿 Git changes detected: ${changedFiles.length} files changed`);
          log('Reindexing changed files...');
          
          const count = await indexMultipleFiles(
            changedFiles,
            vectorDB,
            embeddings,
            config,
            { verbose }
          );
          
          log(`✓ Reindexed ${count} files`);
        } else {
          log('✓ Index is up to date with git state');
        }
      } catch (error) {
        log(`Warning: Failed to check git state on startup: ${error}`);
      }
      
      // Start background polling for git changes
      log(`✓ Git detection enabled (checking every ${config.gitDetection.pollIntervalMs / 1000}s)`);
      
      gitPollInterval = setInterval(async () => {
        try {
          const changedFiles = await gitTracker!.detectChanges();
          
          if (changedFiles && changedFiles.length > 0) {
            log(`🌿 Git change detected: ${changedFiles.length} files changed`);
            log('Reindexing in background...');
            
            // Don't await - run in background
            indexMultipleFiles(
              changedFiles,
              vectorDB,
              embeddings,
              config,
              { verbose }
            ).then(count => {
              log(`✓ Background reindex complete: ${count} files`);
            }).catch(error => {
              log(`Warning: Background reindex failed: ${error}`);
            });
          }
        } catch (error) {
          log(`Warning: Git detection check failed: ${error}`);
        }
      }, config.gitDetection.pollIntervalMs);
    } else {
      if (!gitAvailable) {
        log('Git not available - git detection disabled');
      } else if (!isRepo) {
        log('Not a git repository - git detection disabled');
      }
    }
  } else {
    log('Git detection disabled by configuration');
  }
  
  // Initialize file watching if enabled
  // Priority: CLI flag if explicitly set (true/false), otherwise use config default
  const fileWatchingEnabled = watch !== undefined ? watch : config.fileWatching.enabled;
  
  if (fileWatchingEnabled) {
    log('👀 Starting file watcher...');
    fileWatcher = new FileWatcher(rootDir, config);
    
    try {
      await fileWatcher.start(async (event) => {
        const { type, filepath } = event;
        
        if (type === 'unlink') {
          // File deleted
          log(`🗑️  File deleted: ${filepath}`);
          try {
            await vectorDB.deleteByFile(filepath);
            
            // Update manifest
            const manifest = new ManifestManager(vectorDB.dbPath);
            await manifest.removeFile(filepath);
            
            log(`✓ Removed ${filepath} from index`);
          } catch (error) {
            log(`Warning: Failed to remove ${filepath}: ${error}`);
          }
        } else {
          // File added or changed
          const action = type === 'add' ? 'added' : 'changed';
          log(`📝 File ${action}: ${filepath}`);
          
          // Reindex in background
          indexSingleFile(filepath, vectorDB, embeddings, config, { verbose })
            .catch((error) => {
              log(`Warning: Failed to reindex ${filepath}: ${error}`);
            });
        }
      });
      
      const watchedCount = fileWatcher.getWatchedFiles().length;
      log(`✓ File watching enabled (watching ${watchedCount} files)`);
    } catch (error) {
      log(`Warning: Failed to start file watcher: ${error}`);
      fileWatcher = null;
    }
  }
  
  // Handle shutdown gracefully
  const cleanup = async () => {
    log('Shutting down MCP server...');
    clearInterval(versionCheckInterval);
    if (gitPollInterval) {
      clearInterval(gitPollInterval);
    }
    if (fileWatcher) {
      await fileWatcher.stop();
    }
    process.exit(0);
  };
  
  // Listen for termination signals
  process.on('SIGINT', cleanup);
  process.on('SIGTERM', cleanup);
  
  // Connect to stdio transport
  const transport = new StdioServerTransport();
  
  // Use SDK's transport callbacks for parent process detection
  // This avoids conflicts with the transport's stdin management
  transport.onclose = () => {
    log('Transport closed, parent process likely terminated');
    cleanup().catch(() => process.exit(0));
  };
  
  transport.onerror = (error) => {
    log(`Transport error: ${error}`);
    // Transport will close after error, onclose will handle cleanup
  };
  
  await server.connect(transport);
  
  log('MCP server started and listening on stdio');
}

